/**
 * Offer card shown after an eligible AI-feedback dislike.
 *
 * Describes the review as independent expert verification (never a grade
 * change). Branches on remaining quota: quota > 0 → submission form;
 * quota == 0 → soft upgrade message (no payment UI).
 *
 * @module HumanReviewOfferCard
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useEntitlement } from '@/hooks/useHumanReviews';
import { HumanReviewRequestForm } from './HumanReviewRequestForm';
import type { ReviewModule } from '@/lib/humanReviewsApi';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { requiresRenewal } from '@/lib/planAccess';
import { ROUTES } from '@/lib/routeConstants';

export interface HumanReviewOfferCardProps {
  readonly targetAnswerId: string;
  readonly module: ReviewModule;
  readonly ratingId?: string;
}

export function HumanReviewOfferCard({
  targetAnswerId,
  module,
  ratingId,
}: HumanReviewOfferCardProps) {
  const { t } = useTranslation();
  const { entitlement, isLoading } = useEntitlement();
  const { plan, isLoading: isPlanLoading } = useCurrentPlan();
  const [accepted, setAccepted] = useState(false);

  const remaining = entitlement?.remaining ?? 0;
  const renewalRequired = requiresRenewal(plan);
  const hasQuota = remaining > 0 && !renewalRequired;

  return (
    <div className="mt-3 rounded-lg border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4">
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="font-ui text-sm font-semibold text-foreground">
            {t('humanReview.offer.title')}
          </p>
          <p className="font-ui text-xs leading-relaxed text-muted-foreground">
            {t('humanReview.offer.description')}
          </p>

          {!isLoading && !isPlanLoading && hasQuota && !accepted && (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button type="button" size="sm" onClick={() => setAccepted(true)}>
                {t('humanReview.offer.accept')}
              </Button>
              <span className="font-ui text-xs text-muted-foreground">
                {t('humanReview.offer.remaining', { count: remaining })}
              </span>
            </div>
          )}

          {!isLoading && !isPlanLoading && !hasQuota && (
            <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3">
              <p className="font-ui text-xs font-semibold text-foreground">
                {renewalRequired
                  ? t('humanReview.offer.passExpiredTitle', {
                      defaultValue: 'Your pass has expired',
                    })
                  : t('humanReview.offer.noQuotaTitle')}
              </p>
              <p className="font-ui mt-1 text-xs text-muted-foreground">
                {renewalRequired
                  ? t('humanReview.offer.passExpiredMessage', {
                      defaultValue:
                        'Renew your plan before submitting another human review request.',
                    })
                  : t('humanReview.offer.noQuotaMessage')}
              </p>
              {renewalRequired ? (
                <Button asChild size="sm" className="font-ui mt-3">
                  <Link to={`${ROUTES.SETTINGS}#plan`}>
                    {t('dashboard.planBanner.expired.cta', {
                      defaultValue: 'Renew plan',
                    })}
                  </Link>
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {accepted && hasQuota && (
        <HumanReviewRequestForm
          targetAnswerId={targetAnswerId}
          module={module}
          ratingId={ratingId}
        />
      )}
    </div>
  );
}
